import { BidStatus } from "api";
import { Checkbox, Field } from "components/shared/form";
import React from "react";
import {
    MAX_MATCHING_PERIOD_IN_HOURS,
    MAX_MATCHING_RADIUS_IN_METERS,
    MatchingSettingsForm,
    MIN_MATCHING_PERIOD_IN_HOURS,
    MIN_MATCHING_RADIUS_IN_METERS,
    matchingPeriodValidator,
    matchingRadiusValidator,
    NumericValue,
    selectableStatuses,
    toNumericValue,
    UpdateForm,
} from "./model";

type SettingsCheckboxProps = {
    name: string;
    label: string;
    value: boolean;
    disabled?: boolean;
    onChange?: (checked: boolean) => void;
};

type NumberFieldProps = {
    name: string;
    label: string;
    value: NumericValue;
    disabled: boolean;
    step: string;
    min: number;
    max: number;
    validators: Array<(value?: string) => string>;
    onChange: (value: NumericValue) => void;
};

type FormSectionProps = {
    form: MatchingSettingsForm;
    editable: boolean;
    updateForm: UpdateForm;
};

type SaveFormProps = {
    isSaving: boolean;
    isDirty: boolean;
    isValid: boolean;
    saveError: boolean;
    onSave: () => void;
};

type WaybillMatchingSettingsFormProps = FormSectionProps &
    SaveFormProps & {
        onToggleStatus: (status: BidStatus, checked: boolean) => void;
    };

type ShippingOrderSettingsFormProps = SaveFormProps & {
    disableMatching: boolean;
    editable: boolean;
    onChange: (checked: boolean) => void;
};

type FormActionsProps = SaveFormProps & {
    editable: boolean;
    errorMessage: string;
};

const SettingsCheckbox = ({ name, label, value, disabled = false, onChange }: SettingsCheckboxProps) => (
    <Checkbox
        label={label}
        inputProps={{ name }}
        value={value}
        onChange={disabled ? undefined : ({ value: nextValue }: { value: boolean }) => onChange?.(nextValue)}
        disabled={disabled}
        fieldBeforeLabel
        labelWrapper="col-auto col-form-label col-form-label-sm pl-0 mb-0"
        fieldWrapper="col-auto"
        bWrapper="row flex-nowrap w-100 align-items-center mb-2"
        checkboxWrapCname="my-0"
    />
);

const NumberField = ({ name, label, value, disabled, step, min, max, validators, onChange }: NumberFieldProps) => (
    <Field
        type="number"
        label={label}
        inputProps={{ name, type: "number", step, min, max, disabled }}
        value={value}
        onChange={({ value: nextValue }: { value: unknown }) => onChange(toNumericValue(nextValue))}
        validators={validators}
        labelWrapper="col-12 col-form-label col-form-label-sm text-muted"
        fieldWrapper="col-12 col-md-5"
        bWrapper="row mb-3"
    />
);

const StatusSettingsSection = ({
    form,
    editable,
    updateForm,
    onToggleStatus,
}: Pick<WaybillMatchingSettingsFormProps, "form" | "editable" | "updateForm" | "onToggleStatus">) => (
    <div className="row border-bottom pb-4 mb-4">
        <div className="col-sm-4">
            <h5>Автоматическое сопоставление ЭТрН</h5>
            <p className="small text-muted mb-sm-0">
                Все включённые условия применяются одновременно. Настройки действуют только для текущей организации.
            </p>
        </div>
        <div className="col-sm-8">
            <SettingsCheckbox
                name="disableWaybillMatching"
                label="Отключить автоматическое сопоставление ЭТрН с заявками"
                value={form.disableWaybillMatching}
                disabled={!editable}
                onChange={(checked) => updateForm((current) => ({ ...current, disableWaybillMatching: checked }))}
            />
            <h6>Статусы заявок для поиска</h6>
            {selectableStatuses.map(({ value, label }) => (
                <SettingsCheckbox
                    key={value}
                    name={`status-${value}`}
                    label={label}
                    value={form.bid.allowedStatuses.includes(value)}
                    disabled={!editable || form.disableWaybillMatching || value === BidStatus.Started}
                    onChange={(checked) => onToggleStatus(value, checked)}
                />
            ))}
            <p className="small text-muted mt-1 mb-0">
                Активные заявки всегда участвуют в поиске, этот статус нельзя отключить.
            </p>
        </div>
    </div>
);

const ShippingOrderSettingsSection = ({
    disableMatching,
    editable,
    onChange,
}: Pick<ShippingOrderSettingsFormProps, "disableMatching" | "editable" | "onChange">) => (
    <div className="row">
        <div className="col-sm-4">
            <h5>Обработка входящих ЭЗЗ</h5>
            <p className="small text-muted mb-sm-0">Настройка действует только для текущей организации.</p>
        </div>
        <div className="col-sm-8">
            <SettingsCheckbox
                name="disableShippingOrderMatching"
                label="Отключить автоматическое создание заказа по входящей ЭЗЗ"
                value={disableMatching}
                disabled={!editable}
                onChange={onChange}
            />
            <p className="small text-muted mb-0">
                Входящая ЭЗЗ останется в реестре без заказа. Её можно будет привязать вручную к существующему заказу или
                заявке.
            </p>
        </div>
    </div>
);

const RequiredConditionsSection = () => (
    <div className="row border-bottom pb-4 mb-4">
        <div className="col-sm-4">
            <h5>Обязательные условия</h5>
        </div>
        <div className="col-sm-8">
            <SettingsCheckbox name="car-number" label="Госномер машины" value disabled />
            <SettingsCheckbox name="driver" label="Водитель" value disabled />
            <p className="small text-muted mb-0">
                Водитель сравнивается последовательно: серия и номер ВУ, затем ИНН, номер телефона и ФИО.
            </p>
        </div>
    </div>
);

const BidConditions = ({ form, editable, updateForm }: FormSectionProps) => (
    <>
        <SettingsCheckbox
            name="useShippingOrderNumber"
            label="Номер ЭЗЗ"
            value={form.useShippingOrderNumber}
            disabled={!editable}
            onChange={(checked) => updateForm((current) => ({ ...current, useShippingOrderNumber: checked }))}
        />
        <p className="small text-muted mt-n1">
            При прямом совпадении номера ЭЗЗ связанная с ним заявка считается полностью совпавшей.
        </p>

        <SettingsCheckbox
            name="useCreateDate"
            label="Дата создания ЭТрН и заявки"
            value={form.bid.useCreateDate}
            disabled={!editable}
            onChange={(checked) =>
                updateForm((current) => ({
                    ...current,
                    bid: { ...current.bid, useCreateDate: checked },
                }))
            }
        />
        <NumberField
            name="createDateMatchingPeriodInHours"
            label="Допустимое отклонение даты создания, ч"
            value={form.bid.createDateMatchingPeriodInHours}
            disabled={!editable || !form.bid.useCreateDate}
            step="1"
            min={MIN_MATCHING_PERIOD_IN_HOURS}
            max={MAX_MATCHING_PERIOD_IN_HOURS}
            validators={[matchingPeriodValidator]}
            onChange={(value) =>
                updateForm((current) => ({
                    ...current,
                    bid: { ...current.bid, createDateMatchingPeriodInHours: value },
                }))
            }
        />

        <SettingsCheckbox
            name="loadPointUsePlanEnterDate"
            label="Плановая дата и время первой точки погрузки"
            value={form.loadPoint.usePlanEnterDate}
            disabled={!editable}
            onChange={(checked) =>
                updateForm((current) => ({
                    ...current,
                    loadPoint: { ...current.loadPoint, usePlanEnterDate: checked },
                }))
            }
        />
        <NumberField
            name="planEnterDateMatchingPeriodInHours"
            label="Допустимое отклонение плановой даты, ч"
            value={form.loadPoint.planEnterDateMatchingPeriodInHours}
            disabled={!editable || !form.loadPoint.usePlanEnterDate}
            step="1"
            min={MIN_MATCHING_PERIOD_IN_HOURS}
            max={MAX_MATCHING_PERIOD_IN_HOURS}
            validators={[matchingPeriodValidator]}
            onChange={(value) =>
                updateForm((current) => ({
                    ...current,
                    loadPoint: { ...current.loadPoint, planEnterDateMatchingPeriodInHours: value },
                }))
            }
        />
    </>
);

const CounterpartyConditions = ({ form, editable, updateForm }: FormSectionProps) => (
    <>
        <SettingsCheckbox
            name="bidUseCounterparty"
            label="Контрагент заявки по ИНН и КПП"
            value={form.bid.useCounterparty}
            disabled={!editable}
            onChange={(checked) =>
                updateForm((current) => ({
                    ...current,
                    bid: { ...current.bid, useCounterparty: checked },
                }))
            }
        />
        <SettingsCheckbox
            name="unloadPointUseCounterparty"
            label="Контрагент точки выгрузки по ИНН и КПП"
            value={form.unloadPoint.useCounterparty}
            disabled={!editable}
            onChange={(checked) =>
                updateForm((current) => ({
                    ...current,
                    unloadPoint: { ...current.unloadPoint, useCounterparty: checked },
                }))
            }
        />
        <p className="small text-muted mt-n1">Если КПП не заполнен, контрагент сопоставляется только по ИНН.</p>
    </>
);

const LocationConditions = ({ form, editable, updateForm }: FormSectionProps) => (
    <>
        <SettingsCheckbox
            name="loadPointUseLocation"
            label="Адрес точки погрузки"
            value={form.loadPoint.useLocation}
            disabled={!editable}
            onChange={(checked) =>
                updateForm((current) => ({
                    ...current,
                    loadPoint: { ...current.loadPoint, useLocation: checked },
                }))
            }
        />
        <NumberField
            name="loadPointMatchingRadius"
            label="Радиус сопоставления точки погрузки, км"
            value={form.loadPoint.matchingRadiusInKilometers}
            disabled={!editable || !form.loadPoint.useLocation}
            step="0.1"
            min={MIN_MATCHING_RADIUS_IN_METERS / 1000}
            max={MAX_MATCHING_RADIUS_IN_METERS / 1000}
            validators={[matchingRadiusValidator]}
            onChange={(value) =>
                updateForm((current) => ({
                    ...current,
                    loadPoint: { ...current.loadPoint, matchingRadiusInKilometers: value },
                }))
            }
        />

        <SettingsCheckbox
            name="unloadPointUseLocation"
            label="Адрес точки выгрузки"
            value={form.unloadPoint.useLocation}
            disabled={!editable}
            onChange={(checked) =>
                updateForm((current) => ({
                    ...current,
                    unloadPoint: { ...current.unloadPoint, useLocation: checked },
                }))
            }
        />
        <NumberField
            name="unloadPointMatchingRadius"
            label="Радиус сопоставления точки выгрузки, км"
            value={form.unloadPoint.matchingRadiusInKilometers}
            disabled={!editable || !form.unloadPoint.useLocation}
            step="0.1"
            min={MIN_MATCHING_RADIUS_IN_METERS / 1000}
            max={MAX_MATCHING_RADIUS_IN_METERS / 1000}
            validators={[matchingRadiusValidator]}
            onChange={(value) =>
                updateForm((current) => ({
                    ...current,
                    unloadPoint: { ...current.unloadPoint, matchingRadiusInKilometers: value },
                }))
            }
        />
    </>
);

const AdditionalConditionsSection = ({ form, editable, updateForm }: FormSectionProps) => (
    <div className="row">
        <div className="col-sm-4">
            <h5>Дополнительные условия</h5>
        </div>
        <div className="col-sm-8">
            <BidConditions form={form} editable={editable} updateForm={updateForm} />
            <CounterpartyConditions form={form} editable={editable} updateForm={updateForm} />
            <LocationConditions form={form} editable={editable} updateForm={updateForm} />
        </div>
    </div>
);

const FormActions = ({ editable, isSaving, isDirty, isValid, saveError, onSave, errorMessage }: FormActionsProps) => (
    <>
        {saveError && (
            <div className="alert alert-danger py-2" role="alert">
                {errorMessage}
            </div>
        )}
        {editable && (
            <div className="d-flex justify-content-end border-top pt-3 mt-4">
                <button
                    type="button"
                    className="btn btn-brand btn-sm"
                    disabled={!isValid || !isDirty || isSaving}
                    onClick={onSave}
                >
                    {isSaving ? "Сохранение..." : "Сохранить"}
                </button>
            </div>
        )}
    </>
);

export const ShippingOrderSettingsForm = (props: ShippingOrderSettingsFormProps) => (
    <div className="card card-body mb-3">
        <ShippingOrderSettingsSection
            disableMatching={props.disableMatching}
            editable={props.editable}
            onChange={props.onChange}
        />
        <FormActions {...props} errorMessage="Не удалось сохранить настройки обработки ЭЗЗ. Повторите попытку." />
    </div>
);

export const WaybillMatchingSettingsForm = (props: WaybillMatchingSettingsFormProps) => (
    <div className="card card-body mb-3">
        <StatusSettingsSection
            form={props.form}
            editable={props.editable}
            updateForm={props.updateForm}
            onToggleStatus={props.onToggleStatus}
        />
        <RequiredConditionsSection />
        <AdditionalConditionsSection
            form={props.form}
            editable={props.editable && !props.form.disableWaybillMatching}
            updateForm={props.updateForm}
        />
        <FormActions {...props} errorMessage="Не удалось сохранить настройки сопоставления ЭТрН. Повторите попытку." />
    </div>
);
